/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import { Pencil, Plus, Search, Trash, SearchX, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { ScaleLoader } from "react-spinners";
import { Link } from "@/router";
import { useAdminGetForumGroups, useAdminDeleteForumGroup } from "@/services/forumGroupService";
import { objectToQueryString } from "@/utils/objectToQueryString";
import { queryStringToObject } from "@/utils/queryStringToObject";
import { bgColors500 } from "@/utils/stringToTailwindClass";


const initialState = {
    errorMessage: "",
    isLoading: false,
    filters: {
        id: "",
        name: "",
        sidebar_category: "",
        forum_filter: "",
        forum_tag: "",
        hidden: "",
        color: "",
    },
    forum_groups: null,
    page_size: 10,
    page: 1,
    total: 0
};



const ACTIONS = {
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",
    SET_FILTER: "setFilter",
    SET_FILTERS: "setFilters",
    SET_FORUM_GROUPS: "setForumGroups",
    SET_PAGE_SIZE: "setPageSize",
    SET_PAGE: "setPage",
    SET_TOTAL: "setTotal",
};



const reducer = (state, action) => {
  switch (action.type) {
    case "setErrorMessage":
        return { ...state, errorMessage: action.payload };
    case "setIsLoading":
        return { ...state, isLoading: action.payload };
    case "setFilter":
        return { ...state, filters: { ...state.filters, [action.subtype]: action.payload } };
    case "setFilters":
        return { ...state, filters: { ...state.filters, ...action.payload } };
    case "setForumGroups":
        return { ...state, forum_groups: action.payload };
    case "setPageSize":
        return { ...state, page_size: action.payload }
    case "setPage":
        return { ...state, page: action.payload }
    case "setTotal":
        return { ...state, total: action.payload }
    default:
        console.error("Unknown action.type " + action.type);
    throw new Error();
  }
};



export default function Index() {
    const [ , setPageTitle] = useOutletContext();
    const [state, dispatch] = useReducer(reducer, initialState);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const adminGetForumGroups = useAdminGetForumGroups();
    const adminDeleteForumGroup = useAdminDeleteForumGroup();



    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Gestion des groupes de forums");

        return () => {
        setPageTitle("Titre")
        }
    }, [setPageTitle]);



    /* On récupère les filtres de recherche si ils sont présents dans l'URL */
    useEffect(() => {
        if (searchParams) {
            dispatch({ type: ACTIONS.SET_FILTERS, payload: queryStringToObject(searchParams.toString()) });
        }
    }, [searchParams])



    /* On met à jour l'URL quand les filtres changent */
    useEffect(() => {
        if(JSON.stringify(state.filters) !== JSON.stringify(searchParams)) {
            setSearchParams(objectToQueryString(state.filters));
        }
    }, [searchParams, setSearchParams, state.filters])



    /* Lorsque la page charge, si les parametres sont vides, on set skip et limit */
    useEffect(() => {
        if (searchParams.size == 0) {
            setSearchParams("limit=10&skip=0")
        }
    }, [])



    /* Lorsque l'on modifie la taille de la page on rafraichit */
    useEffect(() => {
        if(state?.page_size)
            dispatch({ type: ACTIONS.SET_PAGE, payload: 1 })
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "limit", payload: String(state.page_size) })
    }, [state.page_size])



    /* Lorsque l'on modifie la page on rafraichit */
    useEffect(() => {
        if(state?.page)
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
    }, [state.page])



    /* Lorsque les états skip et limit ont changé, on rafraichit */
    useEffect(() => {
        if (state.total > 0)
            searchHandler()
    }, [state.filters.skip, state.filters.limit])



    /* Handler de la recherche */
    const searchHandler = async () => {
        const filters = state.filters;
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            const [forum_groups, total] = await adminGetForumGroups(filters).withTotal();
            dispatch({ type: ACTIONS.SET_FORUM_GROUPS, payload: forum_groups });
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            dispatch({ type: ACTIONS.SET_TOTAL, payload: total });
        } catch (error) {
            console.error("admin/forum_groups/index::searchHandler", error);
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };



    /* Gestion du bouton de reset des filtres */
    const clearFiltersHandler = () => {
        dispatch({ type: ACTIONS.SET_FILTERS, payload: initialState.filters });
    }



    /* Handler de la suppression d'un groupe de forums */
    const deleteForumGroupHandler = async (forum_group) => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            await adminDeleteForumGroup(forum_group.id);
            searchHandler();
        } catch (error) {
            console.error('admin/forum_groups/index::deleteForumGroupHandler::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };




    return (
        <>
            {/* Filtres de recherche */}

            <h2 className="text-secondary text-lg flex-none content-center">Filtres de recherche</h2>
            <div className="flex flex-wrap w-full justify-start gap-4">
                <div className="flex flex-col justify-between h-12">
                    <label className="text-primary text-sm" htmlFor="searchID">
                        ID
                    </label>
                    <input
                        type="text"
                        id="searchID"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="ID"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "id", payload: e.target.value })}
                        value={state.filters.id}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchName">
                        Nom
                    </label>
                    <input
                        type="text"
                        id="searchName"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Nom"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "name", payload: e.target.value })}
                        value={state.filters.name}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchSidebarCategory">
                        Sidebar
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchSidebarCategory" id="searchSidebarCategory"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "sidebar_category", payload: e.target.value })}
                        value={state.filters.sidebar_category}
                    >
                        <option value="">-</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                    </select>
                </div>
                
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchForumFilter">
                        Filtre de forum
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchForumFilter" id="searchForumFilter"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "forum_filter", payload: e.target.value })}
                        value={state.filters.forum_filter}
                    >
                        <option value="">-</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                    </select>
                </div>

                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchForumTag">
                        Tag
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchForumTag" id="searchForumTag"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "forum_tag", payload: e.target.value })}
                        value={state.filters.forum_tag}
                    >
                        <option value="">-</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                    </select>
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchHidden">
                        Caché
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchHidden" id="searchHidden"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "hidden", payload: e.target.value })}
                        value={state.filters.hidden}
                    >
                        <option value="">-</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                    </select>
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchColor">
                        Couleur
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchColor" id="searchColor"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "color", payload: e.target.value })}
                        value={state.filters.color}
                    >
                        <option value="">-</option>
                        {Object.keys(bgColors500).map((color, i) => {
                            return (
                                <option key={i} value={color}>
                                    {color}
                                </option>
                            )
                        })}
                    </select>
                </div>
                <div className="flex flex-col justify-end">
                    <button 
                        id="searchButton" name="searchButton"
                        className="pb-1" onClick={() => {dispatch({ type: ACTIONS.SET_PAGE, payload: 1 }); searchHandler()}}
                        title="Rechercher">
                        <Search size={32} className="text-primary hover:text-blue-600" />
                    </button>
                </div>
                <div className="flex flex-col justify-end">
                    <button 
                        id="clearFiltersButton" name="clearFiltersButton"
                        className="pb-1" onClick={() => clearFiltersHandler()}
                        title="Nettoyer la recherche">
                        <SearchX size={20} className="text-primary hover:text-red-600" />
                    </button>
                </div>
            </div>

            {/* Liste des groupes de forums */}

            <div className="flex flex-wrap w-full h-14 justify-between mb-3 ">
                <div className="flex content-center items-center gap-2">
                    <h2 className="text-secondary text-lg flex-none content-center">Liste des groupes de forums</h2>
                    <select className="border-1 border-gray-400 py-1 text-sm text-secondary h-10"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_PAGE_SIZE, payload: e.target.value })} 
                        value={state.page_size}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
                <div className="flex flex-wrap content-center">
                    {state.errorMessage && (
                        <span className="text-red-700 flex-none content-center mr-4">{state.errorMessage}</span>
                    )}
                    {state.isLoading ? <ScaleLoader height={20} color={"#696969"} cssOverride={{alignSelf: "center"}} /> : null}
                    <Link
                        to="/admin/forum_groups/add"
                        state={{ from: location}}
                        className="ml-4 border rounded-lg h-10 w-10 border-secondary border-dashed hover:bg-blue-100 content-center"
                    >
                        <Plus className="mx-auto" />
                    </Link>
                </div>
            </div>
            <table className="table-auto w-full border border-separate rounded-xl border-blue-500 border-spacing-0">
                <thead className="bg-blue-200">
                    <tr className="h-10">
                        <th className="px-4 py-2 text-left rounded-tl-xl">Nom</th>
                        <th className="px-4 py-2 text-center">Description</th>
                        <th className="px-4 py-2 text-center">Compétition</th>
                        <th className="px-4 py-2 text-center"># Forums</th>
                        <th className="px-4 py-2 text-center">Paramètres</th>
                    </tr>
                </thead>
                <tbody>
                    {state.forum_groups?.length > 0 
                        ? ( state.forum_groups.map((forum_group, i) => {
                            return (
                                <tr
                                    key={forum_group.id}
                                    className={`h-12 ${i % 2 === 0 && "bg-blue-100"} hover:bg-sky-200 `}
                                >
                                    <th className={`text-left px-1 ${i === state.forum_groups.length - 1 && "rounded-bl-xl"}`}>
                                        <div className="text-secondary text-xs flex items-center gap-2">
                                            <span className="content-center">ID:{forum_group.id}</span>
                                            <button
                                                onDoubleClick={() => deleteForumGroupHandler(forum_group)}
                                                title="Double-cliquez pour supprimer le groupe de forums"
                                            >
                                                <Trash size={10} className="text-red-500 hover:text-red-700" />
                                            </button>
                                        </div>
                                        <div className="text-primary text-base flex items-center">
                                            <Link to={"/admin/forum_groups/" + forum_group.id} state={{ from: location}}>
                                                <Pencil size={12} className="flex content-center mr-2" />
                                            </Link>

                                            <span className="flex content-center font-bold">
                                                {forum_group.name}
                                            </span>
                                        </div>
                                    </th>
                                    <td className="text-center text-wrap">
                                        <span className="text-xs text-secondary text-wrap">{forum_group?.forum_description}</span>
                                    </td>
                                    <td className="text-center text-wrap">
                                        <span className="text-xs text-secondary text-wrap">{forum_group?.competition?.name}</span>
                                    </td>
                                    <td className="text-center text-wrap">
                                        <span className="text-xs text-secondary text-wrap underline">
                                            <Link to={"/admin/forums?skip=0&limit=10&group_id=" + forum_group.id} state={{ from: location}}>
                                                {forum_group?.forum_count}
                                            </Link>
                                        </span>
                                    </td>
                                    <td className={`px-4 py-2 text-center ${i === state.forum_groups.length - 1 && "rounded-br-xl"}`}>
                                        <div className="flex flex-row flex-wrap justify-center w-full gap-1 items-center">
                                            <div className="text-xs text-secondary flex flex-row items-center gap-1 flex-nowrap">
                                                <span className="text-xs text-secondary">Sidebar :</span>
                                                {forum_group.sidebar_category ? <Check size={12} className="text-green-700" /> : <X size={12} className="text-red-700" />}
                                            </div>
                                            <div className="text-xs text-secondary flex flex-row items-center gap-1 flex-nowrap">
                                                <span className="text-xs text-secondary">Filtre :</span>
                                                {forum_group.forum_filter ? <Check size={12} className="text-green-700" /> : <X size={12} className="text-red-700" />}
                                            </div>
                                            <div className="text-xs text-secondary flex flex-row items-center gap-1 flex-nowrap">
                                                <span className="text-xs text-secondary">Tag :</span>
                                                {forum_group.forum_tag ? <Check size={12} className="text-green-700" /> : <X size={12} className="text-red-700" />}
                                            </div>
                                            <div className="text-xs text-secondary flex flex-row items-center gap-1 flex-nowrap">
                                                <span className="text-xs text-secondary">Caché :</span>
                                                {forum_group.hidden ? <Check size={12} className="text-green-700" /> : <X size={12} className="text-red-700" />}
                                            </div>
                                            <div className="text-xs text-secondary flex flex-row items-center gap-1 flex-nowrap">
                                                <span className="text-xs text-secondary">Couleur :</span>
                                                {forum_group.color ? <div className={`w-2 h-2 rounded-full ${bgColors500[forum_group.color]}`} /> : <X size={12} className="text-red-700" />}
                                            </div>
                                            <div className="text-xs text-secondary flex flex-row items-center gap-1 flex-nowrap">
                                                <span className="text-xs text-secondary">Droits :</span>
                                                <span className="text-xs text-secondary">{forum_group.forum_rights.length || 0}</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })) : state.forum_groups ? (
                            <tr>
                                <td colSpan={3} className="text-center text-secondary py-3">
                                    Aucun groupe de forums trouvé
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td colSpan={3} className="text-center text-secondary py-3">
                                    Lancez la recherche pour afficher les groupes de forums
                                </td>
                            </tr>
                        )}
                </tbody>
            </table>
            { state.total > 0 &&
                <div className="w-full flex flex-row gap-5 justify-end mt-4">
                                      
                    <div className="flex flex-row gap-2 items-center border-collapse">
                        
                        <button 
                            className="text-base border border-gray-400 px-2 rounded-s-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                            disabled={state.page <= 1 || state.isLoading}
                            onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page - 1 })}}
                        >
                            <ChevronLeft size={24} />
                        </button>

                        <span className="text-secondary text-base border border-gray-400 px-2 border-collapse">
                            {state.page} / {Math.ceil(state.total / state.page_size)}
                        </span>

                        <button 
                            className="text-base border border-gray-400 px-2 rounded-e-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                            disabled={state.page >= Math.ceil(state.total / state.page_size) || state.isLoading}
                            onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page + 1 })}}
                        >
                            <ChevronRight size={24} />
                        </button>

                    </div>
                </div>
            }
        </>
    )
}
